import classNames from "classnames";
import {
    DefaultCellRightAlign,
    IndexCell,
    SearchKeywordCell,
    TrafficShare,
    UrlCellWebsiteAnalysis,
    KeywordAnalysisPosition,
} from "app/components/React/Table/cells";
import { DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    EndCellContainer,
    KeywordsCountContainer,
    StyledEnrichButton,
    StyledEnrichButtonText,
} from "app/components/keyword-research/website-search-ads/styledComponents";
import { SearchAdsPageAdUnit } from "app/components/keyword-research/website-search-ads/SearchAdsPageAdUnit";
import { RowSelectionConsumer } from "app/components/React/Table/cells/RowSelection";
import { SelectAllRowsHeaderCellConsumer } from "app/components/React/Table/headerCells/SelectAllRowsHeaderCell";
import { hasDigitalMarketingPermission, hasMarketResearchPermission } from "app/components/SideBar/utils";
import { allTrackers } from "app/services/track/track";

const onUrlClicked = (site: string) => {
    // same logic as in UrlCell (UrlCell is used in UrlCellWebsiteAnalysis)
    let url: string;
    try {
        url = decodeURIComponent(site);
    } catch (e) {
        url = site;
    }
    if (url === "N/A" || !url) {
        url = "-";
    }
    allTrackers.dispatchTrackEvent("External Link", "click", `Table/URL/${url || ""}`);
};

const onTopKeywordItemClick = (keyword: string) => {
    allTrackers.dispatchTrackEvent("Internal Link", "click", `Table/Analyze KW/${keyword || ""}`);
};

const onKeywordItemClick = (keyword: string) => {
    allTrackers.dispatchTrackEvent("Internal Link", "click", `Analyze KW/${keyword || ""}`);
};

export const tableColumns = {
    DEFAULT_SORT: "TrafficShare",
    DEFAULT_SORT_DIRECTION: "desc",
    getColumns: (
        sortedColumn = {
            field: tableColumns.DEFAULT_SORT,
            sortDirection: tableColumns.DEFAULT_SORT_DIRECTION,
        },
    ) => {
        return [
            {
                fixed: true,
                cellComponent: IndexCell,
                disableHeaderCellHover: true,
                sortable: false,
                width: 54,
                isResizable: false,
                cellClass: "pla-ads-index",
                dashboardHide: true,
            },
            {
                field: "AdDetails",
                displayName: i18nFilter()("pla.ads.page.ads.table.columns.addetails.title"),
                tooltip: i18nFilter()("pla.ads.page.ads.table.columns.addetails.title.tooltip"),
                cellComponent: SearchAdsPageAdUnit,
                minWidth: 427,
                showTotalCount: true,
                cellClass: "pla-ads-ads",
                isResizable: true,
                sortable: false,
            },
            {
                field: "TrafficShare",
                displayName: i18nFilter()("pla.ads.page.ads.table.columns.share.title"),
                tooltip: i18nFilter()("pla.ads.page.ads.table.columns.share.title.tooltip"),
                cellComponent: (props: any) => {
                    return (
                        <div className="pla-ads-traffic-share">
                            <TrafficShare {...props} />
                        </div>
                    );
                },
                cellClass: "pla-ads-traffic",
                sortable: true,
                minWidth: 117,
            },
            {
                field: "Volume",
                displayName: i18nFilter()("pla.ads.page.ads.table.columns.volume.title"),
                tooltip: i18nFilter()("pla.ads.page.ads.table.columns.volume.title.tooltip"),
                headerComponent: DefaultCellHeaderRightAlign,
                cellComponent: DefaultCellRightAlign,
                format: "swPosition",
                cellClass: "pla-ads-volume",
                sortable: true,
                minWidth: 100,
            },
            {
                field: "LandingPage",
                displayName: i18nFilter()("pla.ads.page.ads.table.columns.pageUrl.title"),
                tooltip: i18nFilter()("pla.ads.page.ads.table.columns.pageUrl.title.tooltip"),
                cellComponent: (props: any) => {
                    return (
                        <div onClick={() => onUrlClicked(props?.value)}>
                            <UrlCellWebsiteAnalysis {...props} />
                        </div>
                    );
                },
                cellClass: "url-cell pla-ads-destination-url",
                minWidth: 210,
                sortable: false,
            },
            {
                field: "TopKeyword",
                displayName: i18nFilter()("pla.ads.page.ads.table.columns.topKeyword.title"),
                tooltip: i18nFilter()("pla.ads.page.ads.table.columns.topKeyword.title.tooltip"),
                cellComponent: (props: any) => {
                    return (
                        <SearchKeywordCell
                            {...props}
                            destinationState="organicsearch_keywordAnalysis_overview_2"
                            onItemClick={onTopKeywordItemClick}
                            withAdsLink={false}
                        />
                    );
                },
                cellClass: "pla-ads-top-kw",
                minWidth: 130,
                sortable: false,
            },
            {
                field: "KeywordsCount",
                displayName: i18nFilter()("pla.ads.page.ads.table.columns.keywordCount.title"),
                tooltip: i18nFilter()("pla.ads.page.ads.table.columns.keywordCount.title.tooltip"),
                cellComponent: ({ onCellClick, value, ...props }: any) => {
                    const onClick = (e: Event) => {
                        e.stopPropagation();
                        allTrackers.dispatchTrackEvent("Table", "expand", "SearchAds/Keywords/Expand");
                        onCellClick(e, props.row?.index);
                    };
                    const isDisabled = value === "grid.upgrade";
                    const keyword = isDisabled
                        ? i18nFilter()("pla.ads.page.ads.table.columns.topKeyword.title")
                        : value;
                    return (
                        <EndCellContainer>
                            <KeywordsCountContainer>{keyword}</KeywordsCountContainer>
                            <div>
                                {!props.row.parent &&
                                (hasMarketResearchPermission() || hasDigitalMarketingPermission()) ? (
                                    <StyledEnrichButton
                                        height={24}
                                        onClick={isDisabled ? undefined : onClick}
                                        className={classNames("enrich", isDisabled && "enrich--disabled")}
                                        iconName={isDisabled ? "locked" : "chev-down"}
                                        isDisabled={isDisabled}
                                    >
                                        <StyledEnrichButtonText isDisabled={isDisabled}>
                                            {i18nFilter()(
                                                "pla.ads.page.ads.table.columns.keywordCount.enrich.button.title",
                                            )}
                                        </StyledEnrichButtonText>
                                    </StyledEnrichButton>
                                ) : null}
                            </div>
                        </EndCellContainer>
                    );
                },
                minWidth: 166,
                sortable: true,
                dashboardHide: true,
            },
            {
                field: "Position",
                displayName: i18nFilter()("pla.ads.page.ads.table.columns.position.title"),
                tooltip: "pla.ads.page.ads.table.columns.position.title.tooltip",
                minWidth: 70,
                sortable: true,
                cellComponent: KeywordAnalysisPosition,
                lastColumnFixed: true,
                isResizable: false,
                dashboardHide: true,
            },
        ].map((col) => {
            const isSorted = col.field === sortedColumn.field;
            return {
                ...col,
                isSorted,
                sortDirection: isSorted ? sortedColumn.sortDirection : tableColumns.DEFAULT_SORT_DIRECTION,
                isResizable: col.isResizable !== false,
                visible: true,
            };
        });
    },
};

const keywordsTableColumns = {
    DEFAULT_SORT: "TrafficShare",
    DEFAULT_SORT_DIRECTION: "desc",
    getColumns: () => {
        return [
            {
                fixed: true,
                cellComponent: RowSelectionConsumer,
                sortable: false,
                headerComponent: SelectAllRowsHeaderCellConsumer,
                isResizable: false,
                width: 40,
                visible: true,
            },
            {
                fixed: true,
                cellComponent: IndexCell,
                disableHeaderCellHover: true,
                sortable: false,
                width: 40,
                isResizable: false,
            },
            {
                field: "Keyword",
                displayName: i18nFilter()("pla.ads.page.ads.inner.table.columns.keyword.title"),
                tooltip: i18nFilter()("pla.ads.page.ads.inner.table.columns.keyword.title.tooltip"),
                cellComponent: (props: any) => (
                    <SearchKeywordCell
                        {...props}
                        onItemClick={onKeywordItemClick}
                        withAdsLink={false}
                    />
                ),
                sortable: false,
                showTotalCount: true,
                minWidth: 120,
            },
            {
                field: "TrafficShare",
                displayName: i18nFilter()("pla.ads.page.ads.inner.table.columns.keywordShare.title"),
                tooltip: i18nFilter()("pla.ads.page.ads.inner.table.columns.keywordShare.title.tooltip"),
                cellComponent: TrafficShare,
                sortable: true,
                minWidth: 120,
            },
            {
                field: "Volume",
                displayName: i18nFilter()("pla.ads.page.ads.inner.table.columns.volume.title"),
                tooltip: i18nFilter()("pla.ads.page.ads.inner.table.columns.volume.title.tooltip"),
                headerComponent: DefaultCellHeaderRightAlign,
                cellComponent: DefaultCellRightAlign,
                format: "swPosition",
                sortable: true,
                minWidth: 120,
            },
            {
                field: "Cpc",
                displayName: i18nFilter()("pla.ads.page.ads.inner.table.columns.cpc.title"),
                tooltip: i18nFilter()("pla.ads.page.ads.inner.table.columns.cpc.title.tooltip"),
                headerComponent: DefaultCellHeaderRightAlign,
                cellComponent: DefaultCellRightAlign,
                format: "CPC",
                sortable: true,
                minWidth: 120,
            },
        ].map((col) => {
            const isSorted = col.field === keywordsTableColumns.DEFAULT_SORT;
            return {
                ...col,
                isSorted,
                sortDirection: keywordsTableColumns.DEFAULT_SORT_DIRECTION,
                isResizable: col.isResizable !== false,
                visible: true,
            };
        });
    },
};
